import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './ui/Modal';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { GitBranch, Info, Loader } from 'lucide-react';
import Tooltip from './ui/Tooltip';
import { getProvider } from '../services/git/providers';
import { listGitAccounts, hostOf } from '../services/git/gitAccounts';
import { normalizeRemoteUrl, ownerRepoOf } from '../services/git/providers/remote';
import { useVaultStore } from '../store/vaultStore';
import VaultUnlockInline from './git/VaultUnlockInline';
import { progressText } from '../services/git/scopeProgress';

function slugifyRepo(name) {
  return String(name || 'workspace')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90) || 'workspace';
}

/**
 * Every account a workspace can be connected through: the linked GitHub
 * account and each account added on the Git page (GitLab, …) — card 6.
 * @returns {Promise<Array<{ key, provider, accountId, label, login }>>}
 */
/** Vault items that can sign in to a git server, as picker options. */
export function vaultSignInOptions(credentials = []) {
  return credentials.map((it) => ({ value: it.id, label: `${it.name} ${it.type === 'sftp' ? '(SSH)' : '(HTTPS token)'}` }));
}

const providerLabel = (id) => {
  try {
    return getProvider(id).label;
  } catch {
    return id;
  }
};

export async function connectableAccounts() {
  const out = [];
  // GitHub: ONE account per device — a personal access token when there is one, else the browser link.
  const gh = await getProvider('github').getAccount().catch(() => null);
  if (gh?.connected) out.push({ key: 'github', provider: 'github', accountId: gh.id || null, label: `GitHub · ${gh.login}`, login: gh.login });
  for (const a of await listGitAccounts().catch(() => [])) {
    if (a.provider === 'github') continue; // already above
    const label = providerLabel(a.provider);
    out.push({ key: a.id, provider: a.provider, accountId: a.id, label: `${label} · ${a.login} (${hostOf(a.server)})`, login: a.login, server: a.server });
  }
  // Any server at all (Gitea, Bitbucket, a company server…): URL + a Vault item.
  out.push({ key: 'remote', provider: 'remote', accountId: null, label: 'Other git server (URL + Vault sign-in)', login: '' });
  return out;
}

/** Vault items that can sign in to git: Password (HTTPS token) and SFTP (SSH key). Names only. */
const GIT_CAPABLE = new Set(['password', 'sftp']);

/** "Track on this device" (T4): a local history, no remote. */
export const LOCAL_OPTION = Object.freeze({ key: 'local', provider: 'local', accountId: null, label: 'This device only (no remote)', login: '' });

/**
 * The picker's options: "Add a remote" and a team's boards & documents
 * offer only real remotes; a workspace's and the personal scope's Connect
 * (`allowLocal`) add "This device only", the default only when no account is
 * signed in (GitHub
 * Desktop's "Create a local repository" sits beside "Publish" the same way).
 * @returns {{ list, initial }}
 */
export function connectOptions(accounts, { mode = 'connect', allowLocal = false } = {}) {
  const remotes = accounts || [];
  if (mode === 'addRemote' || !allowLocal) return { list: remotes, initial: remotes[0] || null };
  const list = [...remotes, LOCAL_OPTION];
  const signedIn = remotes.some((a) => a.provider !== 'remote');
  return { list, initial: signedIn ? remotes[0] : LOCAL_OPTION };
}

/** "Reading boards · 12 of 40" with a thin bar when the step is counted. */
function ConnectProgress({ progress }) {
  const counted = progress.total > 0;
  const pct = counted ? Math.round((Math.min(progress.done || 0, progress.total) / progress.total) * 100) : 0;
  return (
    <div className="git-connect-progress" role="status" aria-live="polite" data-testid="git-connect-progress">
      <span className="text-xs" style={{ opacity: 0.75 }}>
        {progressText(progress)}
      </span>
      {counted && (
        <div style={{ height: 3, borderRadius: 2, background: 'rgba(161,161,170,.18)', overflow: 'hidden', marginTop: 6 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: 'rgb(var(--accent-rgb, 136, 105, 235))', transition: 'width .2s ease' }} />
        </div>
      )}
    </div>
  );
}

function submitLabel(loading, isLocal, addingRemote) {
  if (isLocal) return loading ? 'Starting…' : 'Start tracking';
  if (addingRemote) return loading ? 'Pushing…' : 'Add & push';
  return loading ? 'Connecting…' : 'Connect & push';
}

/**
 * Connect a workspace's YAML mirror to a repository on any connected account
 * (GitHub, GitLab — gitlab.com or self-managed). The repository is created
 * when it is not there yet.
 */
export default function GitConnectModal({
  isOpen,
  onClose,
  workspace,
  onConnect, // ({ owner, repo, isPrivate, provider, accountId }) => Promise — or ({ provider: 'local' })
  onOpenGitPage,
  loading = false,
  // What a scope's operation is doing now ({ step, done, total }, scopeProgress.js) — shown under the button.
  progress = null,
  subtitle,
  // 'connect' | 'addRemote' (a workspace tracked on this device gets a remote, T6)
  mode = 'connect',
  // Offer "This device only (no remote)" — workspaces and the personal scope (T4, T10).
  allowLocal = false,
  // Why there is no local option here (a team scope, T10): shown in a Tooltip.
  localUnavailable = '',
}) {
  const addingRemote = mode === 'addRemote';
  const [accounts, setAccounts] = useState(null);
  const [choice, setChoice] = useState('');
  const [owner, setOwner] = useState('');
  const [repo, setRepo] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [error, setError] = useState('');
  const [remoteUrl, setRemoteUrl] = useState('');
  const [vaultItemId, setVaultItemId] = useState('');
  const vaultItems = useVaultStore((s) => s.items);
  const vaultUnlocked = useVaultStore((s) => Boolean(s.status?.unlocked));
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setAccounts(null);
    setRepo(slugifyRepo(workspace?.name));
    setIsPrivate(true);
    setError('');
    setRemoteUrl('');
    setVaultItemId('');
    // The Vault list as it is now (names only): items added since the Vault
    // panel was last opened must be choosable too.
    Promise.resolve(useVaultStore.getState().refresh?.()).catch(() => {});
    connectableAccounts().then((found) => {
      const { list, initial } = connectOptions(found, { mode, allowLocal });
      setAccounts(list);
      setChoice(initial?.key || '');
      setOwner(initial?.login || '');
    });
  }, [isOpen, workspace, mode, allowLocal]);

  if (!isOpen || !workspace) return null;
  const selected = (accounts || []).find((a) => a.key === choice) || null;
  const isRemote = selected?.provider === 'remote';
  const isLocal = selected?.provider === 'local';
  const signedIn = (accounts || []).filter((a) => a.provider !== 'remote' && a.provider !== 'local');
  const secretsWarning = addingRemote && Boolean(workspace.git?.secretsAllowed);
  const credentials = (vaultItems || []).filter((it) => GIT_CAPABLE.has(it.type));

  const pick = (key) => {
    const next = (accounts || []).find((a) => a.key === key);
    // The owner follows the account unless the user typed a group/org of their own.
    if (next && (!owner || owner === selected?.login)) setOwner(next.login);
    setChoice(key);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (isLocal) {
      try {
        await onConnect?.({ provider: 'local' });
      } catch (err) {
        setError(err?.message || String(err));
      }
      return;
    }
    if (isRemote) {
      try {
        const url = normalizeRemoteUrl(remoteUrl);
        const item = credentials.find((it) => it.id === vaultItemId);
        if (!item) throw new Error('Choose the Vault item to sign in with.');
        const ref = ownerRepoOf(url);
        await onConnect?.({ ...ref, isPrivate: true, provider: 'remote', url, vaultItemId: item.id, vaultItemName: item.name });
      } catch (err) {
        setError(err?.message || String(err));
      }
      return;
    }
    if (!owner.trim() || !repo.trim()) {
      setError('Owner and repository name are required.');
      return;
    }
    try {
      await onConnect?.({
        owner: owner.trim(),
        repo: repo.trim(),
        isPrivate,
        provider: selected?.provider || 'github',
        accountId: selected?.accountId || undefined,
      });
    } catch (err) {
      setError(err?.message || String(err));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={addingRemote ? 'Add a remote' : 'Connect to a repository'}
      subtitle={subtitle || (addingRemote ? 'Push the versions saved on this device to a repository' : 'Keep this workspace in your own git repository')}
      customIcon={<GitBranch size={22} />}
      preventBackdropClose={loading}
    >
      {accounts === null ? (
        <p className="modal-header-subtitle" style={{ fontSize: '0.875rem' }}>Looking for your accounts…</p>
      ) : (
      <>
      {signedIn.length === 0 && !isLocal && (
        <div className="space-y-4" data-testid="git-connect-no-account" style={{ marginBottom: '1rem' }}>
          <p className="modal-header-subtitle" style={{ fontSize: '0.875rem' }}>
            Sign in to GitHub, or add a GitLab account, to keep workspaces in a repository.
          </p>
          {onOpenGitPage && (
            <button
              type="button"
              onClick={onOpenGitPage}
              className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              <span>Sign in on the Git page</span>
            </button>
          )}
          <p className="text-xs" style={{ opacity: 0.7 }}>…or use any other git server below, with a sign-in from your Vault.</p>
        </div>
      )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {isLocal ? (
            <p className="modal-header-subtitle" style={{ fontSize: '0.875rem' }}>
              Track{' '}
              <strong className="modal-header-title" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {workspace.name}
              </strong>{' '}
              in a git history on this device.
            </p>
          ) : (
          <p className="modal-header-subtitle" style={{ fontSize: '0.875rem' }}>
            {addingRemote ? 'Push every saved version of' : 'Sync'}{' '}
            <strong className="modal-header-title" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
              {workspace.name}
            </strong>{' '}
            to a repository.{' '}
            {isRemote ? 'The repository must already exist on that server.' : "If it doesn't exist yet, it will be created."}
          </p>
          )}

          {secretsWarning && (
            <p className="text-xs" role="note" data-testid="git-add-remote-secrets" style={{ color: 'var(--warning, #d97706)' }}>
              You chose “Commit anyway” for a possible credential on this device. Older saved versions may contain those values and are pushed too.
            </p>
          )}

          {accounts.length > 1 && (
            <div>
              <span className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
                Account
              </span>
              <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                <CompactSelectPicker
                  value={choice}
                  onChange={pick}
                  options={accounts.map((a) => ({ value: a.key, label: a.label }))}
                  disabled={loading}
                  aria-label="Account"
                />
              </div>
            </div>
          )}

          {localUnavailable && !addingRemote && (
            <p className="text-xs" data-testid="git-connect-no-local" style={{ opacity: 0.7 }}>
              <Tooltip content={localUnavailable} side="bottom">
                <span tabIndex={0} aria-label={localUnavailable} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Info size={12} />
                  No “This device only” here
                </span>
              </Tooltip>
            </p>
          )}

          {isLocal ? (
            <p className="text-xs" data-testid="git-connect-local-note" style={{ opacity: 0.8 }}>
              Versions stay on this device only. They do not reach your other devices. You can add GitHub, GitLab or another server later.
            </p>
          ) : isRemote ? (
            <>
              <div>
                <label htmlFor="git-connect-url" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Repository URL
                </label>
                <input
                  id="git-connect-url"
                  type="text"
                  value={remoteUrl}
                  onChange={(e) => setRemoteUrl(e.target.value)}
                  disabled={loading}
                  placeholder="https://git.example.com/team/api.git or ssh://git@host:22/team/api.git"
                  className="modal-input"
                />
              </div>
              <div>
                <span className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Sign in with (from your Vault)
                </span>
                {!vaultUnlocked ? (
                  <VaultUnlockInline disabled={loading} />
                ) : (
                  <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                    <CompactSelectPicker
                      value={vaultItemId}
                      onChange={setVaultItemId}
                      options={vaultSignInOptions(credentials)}
                      placeholder="Choose a Password or SSH key item…"
                      disabled={loading}
                      aria-label="Sign in with (from your Vault)"
                    />
                  </div>
                )}
                <p className="text-xs" style={{ opacity: 0.7, marginTop: 6 }}>
                  The secret stays in the Vault; Apiboo reads it only to talk to this server.
                </p>
              </div>
            </>
          ) : (
          <>
          <div>
            <label htmlFor="git-connect-owner" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
              Owner {selected?.provider === 'gitlab' ? '(user or group, e.g. team/backend)' : selected?.provider === 'gitea' ? '(user or organization)' : ''}
            </label>
            <input
              id="git-connect-owner"
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              disabled={loading}
              placeholder="username-or-org"
              className="modal-input"
            />
          </div>

          <div>
            <label htmlFor="git-connect-repo" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
              Repository
            </label>
            <input
              id="git-connect-repo"
              type="text"
              value={repo}
              onChange={(e) => setRepo(e.target.value)}
              disabled={loading}
              placeholder="my-workspace"
              className="modal-input"
            />
          </div>

          {/* The app's checkbox, as in Create Workspace (owner, 04.10.2026). */}
          <div className="flex items-start space-x-2 pt-1">
            <input
              type="checkbox"
              id="git-connect-private"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              disabled={loading}
              className="checkbox-brand cursor-pointer mt-0.5"
            />
            <label htmlFor="git-connect-private" className="text-sm text-gray-700 dark:text-zinc-300 cursor-pointer">
              Create as a private repository
            </label>
          </div>
          </>
          )}

          {error && (
            <p className="text-xs" role="status" aria-live="polite" style={{ color: 'var(--danger, #ef4444)' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && <Loader className="animate-spin" size={16} />}
            <span>{submitLabel(loading, isLocal, addingRemote)}</span>
          </button>
          {loading && progress && <ConnectProgress progress={progress} />}
        </form>
      </>
      )}
    </Modal>
  );
}
